// external
import React, { FC, useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Link,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import NorthIcon from '@mui/icons-material/North';
import SouthIcon from '@mui/icons-material/South';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import Battery20Icon from '@mui/icons-material/Battery20';
import SentimentSatisfiedAlt from '@mui/icons-material/SentimentSatisfiedAlt';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, PieChart, Pie, Cell, Tooltip, LabelList } from 'recharts';
import { useNavigate } from 'react-router-dom';

// internal
import useService from '../../../hooks/service.hook';
import AnalyticsService from '../../../services/analytics.service';
import AssignedTrainingsService from '../../../services/assigned-trainings.service';
import UserService from '../../../services/user.service';
import { getMeasurementUnit } from '../../../helpers/general.helpers';
import { toApiDateRange } from '../../../helpers/datetime.helpers';
import { User } from '../../../models/user.models';
import { AthleteAttendanceTeam } from '../../../models/attendance.models';
import { DailyBreakdownItem, StatsResponse, WeekBreakdownItem } from '../../../models/stats.models';
import { isCurrentWeek } from '../../../components/period-summary/period-summary.helpers';
import { palette } from '../../../theme';
import CircularProgressWithLabel from '../../../components/coe-progress-ring/circular-progress-with-label.component';
import { parseAttendanceResponse } from '../../../components/team-attendance-breakdown/team-attendance-breakdown.helpers';
import {
  buildZoneDistributionData,
  ZoneDistributionItem,
} from '../../../components/training-zone-breakdown/training-zone-breakdown.helpers';

import './my-dashboard.page.css';

dayjs.extend(isoWeek);

const getRecentPeriodRange = () => ({
  start: dayjs().startOf('isoWeek').subtract(2, 'week'),
  end: dayjs().endOf('isoWeek'),
});

interface RecentActivity {
  id: number;
  trainingId: number;
  teamLogo: string;
  teamName: string;
  group: string;
  title: string;
  time: string;
  location: string;
  duration: string;
  distance: string;
  complete: boolean;
}

type DailyChartRow = {
  label: string;
  dayKey: string;
  duration: number;
  distance: number;
};

const formatDurationShort = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

const toPercentDiffDisplay = (percentDiff: number | undefined): number | null => {
  if (percentDiff === undefined || !Number.isFinite(percentDiff)) {
    return null;
  }
  return percentDiff;
};

const getWeeklyComparisonWeeks = (
  weeks: WeekBreakdownItem[]
): { current: WeekBreakdownItem | undefined; previous: WeekBreakdownItem | undefined } => {
  const sorted = [...weeks].sort((a, b) => dayjs(a.week).valueOf() - dayjs(b.week).valueOf());
  const currentIndex = sorted.findIndex((week) => isCurrentWeek(week.week));

  if (currentIndex >= 0) {
    return {
      current: sorted[currentIndex],
      previous: currentIndex > 0 ? sorted[currentIndex - 1] : undefined,
    };
  }

  if (sorted.length >= 2) {
    return {
      current: sorted[sorted.length - 1],
      previous: sorted[sorted.length - 2],
    };
  }

  return {
    current: sorted[0],
    previous: undefined,
  };
};

const WeeklyPctChange: FC<{ pct: number | null }> = ({ pct }) => {
  if (pct === null) {
    return (
      <Typography
        variant="body2"
        color={palette.text.secondary}
        sx={{ minHeight: 28, display: 'flex', alignItems: 'center' }}
      >
        —
      </Typography>
    );
  }
  if (pct === 0) {
    return (
      <Typography
        variant="subtitle1"
        fontWeight={700}
        color={palette.text.secondary}
        sx={{ minHeight: 28, display: 'flex', alignItems: 'center' }}
      >
        0%
      </Typography>
    );
  }
  const up = pct > 0;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 28 }}>
      {up ? (
        <NorthIcon sx={{ color: palette.primary.main, fontSize: 22 }} />
      ) : (
        <SouthIcon sx={{ color: palette.error.main, fontSize: 22 }} />
      )}
      <Typography
        variant="subtitle1"
        component="span"
        fontWeight={700}
        sx={{ color: up ? palette.primary.main : palette.error.main }}
      >
        {Math.round(pct)}%
      </Typography>
    </Box>
  );
};

const WEEKLY_FEELING_ANCHORS = [
  { Icon: BedtimeIcon, anchorPercent: 0 },
  { Icon: Battery20Icon, anchorPercent: 25 },
  { Icon: SentimentSatisfiedAlt, anchorPercent: 50 },
  { Icon: FitnessCenterIcon, anchorPercent: 75 },
  { Icon: LocalFireDepartmentIcon, anchorPercent: 100 },
];

const weeklyFeelingFromAverage = (
  averageEffort: number | undefined
): { computedPercent: number; selectedIndex: number } | null => {
  if (averageEffort === undefined || !Number.isFinite(averageEffort)) {
    return null;
  }
  const avg = averageEffort;
  const computedPercent = Math.round((avg / 10) * 100);
  const anchors = WEEKLY_FEELING_ANCHORS.map((a) => a.anchorPercent);
  let selectedIndex = 0;
  let bestDist = Infinity;
  anchors.forEach((anchor, i) => {
    const d = Math.abs(computedPercent - anchor);
    if (d < bestDist) {
      bestDist = d;
      selectedIndex = i;
    }
  });
  return { computedPercent, selectedIndex };
};

const buildDailyChartData = (
  dailyBreakdown: DailyBreakdownItem[] | undefined,
  start: Dayjs,
  end: Dayjs
): DailyChartRow[] => {
  const byDay = new Map<string, DailyBreakdownItem>();
  (dailyBreakdown ?? []).forEach((item) => {
    byDay.set(dayjs(item.day).format('YYYY-MM-DD'), item);
  });

  const rows: DailyChartRow[] = [];
  let cursor = start.startOf('day');
  while (!cursor.isAfter(end, 'day')) {
    const dayKey = cursor.format('YYYY-MM-DD');
    const day = byDay.get(dayKey);
    rows.push({
      label: cursor.format('MMM D'),
      dayKey,
      duration: day?.totalDuration ?? 0,
      distance: day?.totalDistance ?? 0,
    });
    cursor = cursor.add(1, 'day');
  }
  return rows;
};

const renderCustomizedBarLabel = (
  props: { x?: number; y?: number; width?: number; height?: number; value?: number },
  zonesPieData: ZoneDistributionItem[],
  zonesMetric: 'duration' | 'distance'
) => {
  const { x, y, width, height, value } = props;
  const payload = zonesPieData.find((entry) => entry.zone === value);
  return (
    <g>
      <text
        x={(x ?? 0) + (width ?? 0) + 4}
        y={(y ?? 0) + (height ?? 0) / 2 + 7}
        fill="rgba(0, 0, 0, 0.6)"
        textAnchor="right"
        dominantBaseline="bottom"
      >
        {`${payload?.percent.toFixed(2) ?? 0}%`}
      </text>
      <text x={250} y={(y ?? 0) + (height ?? 0) / 2 + 7} fill="black">
        {`${
          zonesMetric === 'duration'
            ? formatDurationShort(payload?.rawValue ?? 0)
            : `${(payload?.rawValue ?? 0).toFixed(1)} ${getMeasurementUnit()}`
        }`}
      </text>
    </g>
  );
};

const MyDashboardPage: FC = () => {
  const analyticsService = useService(AnalyticsService);
  const assignedTrainingsService = useService(AssignedTrainingsService);
  const userService = useService(UserService);
  const navigate = useNavigate();

  const recentPeriodRange = useMemo(() => getRecentPeriodRange(), []);

  const [user, setUser] = useState<User | null>(null);
  const [recentStats, setRecentStats] = useState<StatsResponse | null>(null);
  const [attendanceTeams, setAttendanceTeams] = useState<AthleteAttendanceTeam[]>([]);
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
  const [recentStatsLoading, setRecentStatsLoading] = useState(true);
  const [attendanceLoading, setAttendanceLoading] = useState(true);
  const [recentActivitiesLoading, setRecentActivitiesLoading] = useState(true);
  const [graphMetric, setGraphMetric] = useState<'duration' | 'distance'>('duration');
  const [zonesMetric, setZonesMetric] = useState<'duration' | 'distance'>('duration');

  useEffect(() => {
    const storedUser = window.localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    fetchRecentStats();
    fetchAttendance();
    fetchRecentActivities();
  }, []);

  const fetchRecentStats = () => {
    setRecentStatsLoading(true);
    const { start, end } = toApiDateRange(recentPeriodRange.start, recentPeriodRange.end);

    analyticsService
      .getStats(start, end)
      .then((response) => {
        setRecentStats(response.data.data);
        setRecentStatsLoading(false);
      })
      .catch(() => {
        setRecentStats(null);
        setRecentStatsLoading(false);
      });
  };

  const fetchAttendance = () => {
    setAttendanceLoading(true);
    const { start, end } = toApiDateRange(recentPeriodRange.start, recentPeriodRange.end);

    userService
      .getAthleteAttendance(start, end)
      .then((response) => {
        setAttendanceTeams(parseAttendanceResponse(response.data.data));
        setAttendanceLoading(false);
      })
      .catch(() => {
        setAttendanceTeams([]);
        setAttendanceLoading(false);
      });
  };

  const mapTrainingToRecentActivity = (training: any): RecentActivity | null => {
    const id = Number(training?.id);
    if (!Number.isFinite(id)) {
      return null;
    }

    const rawTime = training?.time;
    const hasValidTime = !!rawTime && dayjs(rawTime).isValid();
    const teamName = training?.team?.name ?? 'Individual Workout';

    return {
      id,
      trainingId: Number(training?.trainingId),
      teamLogo: training?.team?.logo ?? '',
      teamName,
      group: training?.group?.name ?? '',
      title: training?.title ?? 'Training',
      time: hasValidTime ? dayjs(rawTime).format('MMM D - h:mm a') : '—',
      location: training?.location ?? '—',
      complete: training?.complete ?? false,
      duration: formatDurationShort(training?.totalDurationComplete ?? training?.totalDurationPlanned ?? 0),
      distance: `${(Number(training?.totalDistanceComplete ?? training?.totalDistancePlanned ?? 0) || 0).toFixed(
        1
      )} ${getMeasurementUnit()}`,
    };
  };

  const fetchRecentActivities = () => {
    setRecentActivitiesLoading(true);
    const start = dayjs().subtract(7, 'day').startOf('day').toISOString();
    const end = dayjs().endOf('day').toISOString();

    assignedTrainingsService
      .getAssignedTrainings({ start, end })
      .then((response) => {
        const trainings = response.data.data.trainings ?? [];
        const mapped = (Array.isArray(trainings) ? trainings : [])
          .map(mapTrainingToRecentActivity)
          .filter((activity): activity is RecentActivity => activity !== null && activity.complete);

        setRecentActivities(mapped);
        setRecentActivitiesLoading(false);
      })
      .catch(() => {
        setRecentActivities([]);
        setRecentActivitiesLoading(false);
      });
  };

  const dailyChartData = useMemo(
    () => buildDailyChartData(recentStats?.dailyBreakdown, recentPeriodRange.start, recentPeriodRange.end),
    [recentStats, recentPeriodRange]
  );

  const { currentWeek, previousWeek } = useMemo(() => {
    const weeks = recentStats?.weekBreakdown ?? [];
    const comparison = getWeeklyComparisonWeeks(weeks);
    return { currentWeek: comparison.current, previousWeek: comparison.previous };
  }, [recentStats]);

  const weeklyVolumeDiffs = useMemo(
    () => ({
      durationPct: toPercentDiffDisplay(currentWeek?.duration?.percentDiff),
      distancePct: toPercentDiffDisplay(currentWeek?.distance?.percentDiff),
      sessionsPct: toPercentDiffDisplay(currentWeek?.trainings?.percentDiff),
    }),
    [currentWeek]
  );

  const weeklyEffortDiffPct = useMemo(
    () => toPercentDiffDisplay(currentWeek?.effortRatings?.percentDiff),
    [currentWeek]
  );

  const weeklyFeelingState = useMemo(
    () => weeklyFeelingFromAverage(currentWeek?.effortRatings?.average),
    [currentWeek]
  );

  const zonesPieData = useMemo(
    () =>
      buildZoneDistributionData(
        recentStats?.totalDurationBreakdown,
        recentStats?.totalDistanceBreakdown,
        zonesMetric
      ).reverse(),
    [zonesMetric, recentStats]
  );

  const zonesBarChart = useMemo(
    () => (
      <BarChart
        layout="vertical"
        width={350}
        height={150}
        data={zonesPieData}
        barSize={28}
        margin={{
          top: 0,
          right: 150,
          left: 0,
          bottom: 0,
        }}
      >
        <XAxis type="number" hide={true} />
        <YAxis
          type="category"
          dataKey="name"
          interval={0}
          tickLine={false}
          axisLine={false}
          style={{
            fontWeight: 'bold',
            fill: 'black',
          }}
        />
        <Bar dataKey="value" unit="%" radius={[0, 5, 5, 0]}>
          {zonesPieData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
          <LabelList
            dataKey="zone"
            content={(labelProps) =>
              renderCustomizedBarLabel(
                labelProps as {
                  x?: number;
                  y?: number;
                  width?: number;
                  height?: number;
                  value?: number;
                },
                zonesPieData,
                zonesMetric
              )
            }
          />
        </Bar>
      </BarChart>
    ),
    [zonesPieData, zonesMetric]
  );

  const graphDataKey = graphMetric === 'duration' ? 'duration' : 'distance';

  return (
    <div className="my-dashboard-page">
      {/* 1. Recent Stats Header */}
      <Card className="dashboard-header-card">
        <CardContent className="dashboard-header-content">
          <div className="dashboard-header-left">
            <Avatar
              src={user?.profileImage}
              alt={`${user?.firstName} ${user?.lastName}`}
              className="dashboard-avatar"
            />
            <div>
              <Typography variant="h6" className="dashboard-user-name">
                {user ? `${user.firstName} ${user.lastName}` : '—'}
              </Typography>
              <Typography variant="body2" color={palette.text.secondary}>
                {user?.email ?? '—'}
              </Typography>
            </div>
          </div>
          <div className="dashboard-header-right">
            <div className="dashboard-season-row">
              <Typography variant="body1" className="dashboard-season-label">
                Recent Stats
              </Typography>
              <Typography variant="body1" color={palette.text.secondary}>
                {recentPeriodRange.start.format('MMM D YYYY')} - {recentPeriodRange.end.format('MMM D YYYY')}
              </Typography>
            </div>
            {recentStatsLoading ? (
              <CircularProgress size={32} />
            ) : recentStats ? (
              <div className="dashboard-stats-row">
                <div className="dashboard-stat">
                  <Typography variant="h6" className="dashboard-stat-value">
                    {recentStats.totalTrainings ?? 0}
                  </Typography>
                  <Typography variant="caption" color={palette.text.secondary}>
                    Training Sessions
                  </Typography>
                </div>
                <div className="dashboard-stat">
                  <Typography variant="h6" className="dashboard-stat-value">
                    {formatDurationShort(recentStats.totalDuration ?? 0)} hrs
                  </Typography>
                  <Typography variant="caption" color={palette.text.secondary}>
                    Duration
                  </Typography>
                </div>
                <div className="dashboard-stat">
                  <Typography variant="h6" className="dashboard-stat-value">
                    {`${(recentStats.totalDistance ?? 0).toFixed(1)} ${getMeasurementUnit()}`}
                  </Typography>
                  <Typography variant="caption" color={palette.text.secondary}>
                    Distance
                  </Typography>
                </div>
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {/* 2. Recent Training Graph + 3. Recent Zones */}
      <div className="dashboard-row dashboard-row-charts">
        <Card className="dashboard-card dashboard-card-graph">
          <CardContent>
            <div className="dashboard-card-header">
              <Typography variant="overline" className="dashboard-card-title">
                RECENT TRAINING
              </Typography>
              <ToggleButtonGroup value={graphMetric} exclusive onChange={(_, v) => v && setGraphMetric(v)} size="small">
                <ToggleButton value="duration">Duration</ToggleButton>
                <ToggleButton value="distance">Distance</ToggleButton>
              </ToggleButtonGroup>
            </div>
            {recentStatsLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <CircularProgress />
              </Box>
            ) : (
              <ResponsiveContainer
                width="100%"
                height={350}
                style={{ backgroundColor: palette.grey[50], borderRadius: 12 }}
              >
                <BarChart data={dailyChartData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }} barSize={8}>
                  <XAxis
                    dataKey="label"
                    tickLine={false}
                    axisLine={{ stroke: palette.divider }}
                    height={44}
                    tick={{ fontSize: 11, fill: palette.text.primary }}
                    interval="preserveStartEnd"
                    minTickGap={24}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const row = payload[0].payload as DailyChartRow;
                      const rawValue = graphMetric === 'duration' ? row.duration : row.distance;
                      const formatted =
                        graphMetric === 'duration'
                          ? `${formatDurationShort(rawValue)} hr`
                          : `${rawValue.toFixed(1)} ${getMeasurementUnit()}`;
                      return (
                        <Box
                          sx={{
                            backgroundColor: 'background.paper',
                            border: `1px solid ${palette.divider}`,
                            borderRadius: 1,
                            px: 1.5,
                            py: 1,
                            boxShadow: 1,
                          }}
                        >
                          <Typography variant="caption" display="block" color="text.secondary">
                            {row.label}
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {formatted}
                          </Typography>
                        </Box>
                      );
                    }}
                  />
                  <Bar dataKey={graphDataKey} fill={palette.primary.main} radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="dashboard-card dashboard-card-zones">
          <CardContent>
            <div className="dashboard-card-header">
              <Typography variant="overline" className="dashboard-card-title">
                RECENT ZONES
              </Typography>
              <ToggleButtonGroup value={zonesMetric} exclusive onChange={(_, v) => v && setZonesMetric(v)} size="small">
                <ToggleButton value="duration">Duration</ToggleButton>
                <ToggleButton value="distance">Distance</ToggleButton>
              </ToggleButtonGroup>
            </div>
            {recentStatsLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <CircularProgress />
              </Box>
            ) : zonesPieData.length > 0 ? (
              <div className="zones-pie-container">
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={zonesPieData} labelLine={false} dataKey="value" nameKey="name" cx="50%" cy="50%">
                      {zonesPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="zones-legend">{zonesBarChart}</div>
              </div>
            ) : (
              <Typography color={palette.text.secondary}>No zone data</Typography>
            )}
          </CardContent>
        </Card>
      </div>

      {/* 4. Weekly Training Status + 5. Weekly Effort */}
      <div className="dashboard-row">
        <Card className="dashboard-card">
          <CardContent>
            <Typography variant="overline" className="dashboard-card-title">
              WEEKLY TRAINING STATUS
            </Typography>
            {recentStatsLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <CircularProgress />
              </Box>
            ) : currentWeek ? (
              <div className="weekly-status-columns">
                <div className="weekly-status-col">
                  <WeeklyPctChange pct={weeklyVolumeDiffs.durationPct} />
                  <Typography variant="caption" color={palette.text.secondary}>
                    vs last week
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {formatDurationShort(currentWeek.duration?.total ?? 0)} hr
                  </Typography>
                  <Typography variant="caption" color={palette.text.secondary}>
                    {formatDurationShort(previousWeek?.duration?.total ?? 0)} hr
                  </Typography>
                </div>
                <div className="weekly-status-col">
                  <WeeklyPctChange pct={weeklyVolumeDiffs.distancePct} />
                  <Typography variant="caption" color={palette.text.secondary}>
                    vs last week
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {`${(currentWeek.distance?.total ?? 0).toFixed(1)} ${getMeasurementUnit()}`}
                  </Typography>
                  <Typography variant="caption" color={palette.text.secondary}>
                    {`${(previousWeek?.distance?.total ?? 0).toFixed(1)} ${getMeasurementUnit()}`}
                  </Typography>
                </div>
                <div className="weekly-status-col">
                  <WeeklyPctChange pct={weeklyVolumeDiffs.sessionsPct} />
                  <Typography variant="caption" color={palette.text.secondary}>
                    vs last week
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {currentWeek.trainings?.total ?? 0}
                  </Typography>
                  <Typography variant="caption" color={palette.text.secondary}>
                    {previousWeek?.trainings?.total ?? 0}
                  </Typography>
                </div>
              </div>
            ) : (
              <Typography color={palette.text.secondary}>No week data</Typography>
            )}
          </CardContent>
        </Card>

        <Card className="dashboard-card">
          <CardContent>
            <Typography variant="overline" className="dashboard-card-title">
              WEEKLY EFFORT
            </Typography>
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div className="weekly-feeling-row">
                {WEEKLY_FEELING_ANCHORS.map(({ Icon, anchorPercent }, idx) => {
                  const selected = weeklyFeelingState !== null && idx === weeklyFeelingState.selectedIndex;
                  const displayPercent = selected ? weeklyFeelingState.computedPercent : anchorPercent;
                  return (
                    <div key={idx} className={`weekly-feeling-item ${selected ? 'selected' : ''}`}>
                      <span className="weekly-feeling-icon" aria-hidden>
                        <Icon
                          sx={{
                            fontSize: '2rem',
                            color: selected ? palette.primary.main : palette.text.primary,
                          }}
                        />
                      </span>
                      <Typography variant="caption" className={selected ? 'weekly-feeling-percent-selected' : ''}>
                        {displayPercent}%
                      </Typography>
                    </div>
                  );
                })}
              </div>
              <div className="weekly-feeling-effort-diff">
                <WeeklyPctChange pct={weeklyEffortDiffPct} />
                <Typography variant="body2" color={palette.text.secondary} style={{ marginLeft: '8px' }}>
                  in average effort rating vs last week
                </Typography>
              </div>
            </Box>
          </CardContent>
        </Card>
      </div>

      {/* 6. Recent Activities + 7. Team Attendance */}
      <div className="dashboard-row">
        <Card className="dashboard-card">
          <CardContent>
            <div className="activities-header">
              <Typography variant="overline" className="dashboard-card-title">
                RECENT ACTIVITIES
              </Typography>
              <Typography variant="body2" color={palette.text.secondary} className="activities-group-header">
                Last 7 Days
              </Typography>
            </div>
            <div className="activities-list">
              {recentActivitiesLoading ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress />
                </Box>
              ) : recentActivities.length > 0 ? (
                recentActivities.map((act) => (
                  <Box key={act.id} className="activity-card">
                    <div className="activity-card-header">
                      <div className="activity-card-header-left">
                        {act.teamLogo ? (
                          <Avatar src={act.teamLogo} sx={{ width: 24, height: 24 }} />
                        ) : (
                          <div className="activity-team-placeholder" />
                        )}
                        <Typography variant="body2" fontWeight="bold">
                          {act.teamName}
                        </Typography>
                      </div>
                      <Typography variant="caption" fontWeight="bold">
                        {act.group}
                      </Typography>
                    </div>
                    <Link
                      component="button"
                      variant="body1"
                      underline="hover"
                      color={palette.primary.main}
                      className="activity-title activity-title-link"
                      onClick={() => navigate(`/home?trainingId=${act.trainingId}&trainingType=athlete`)}
                    >
                      {act.title}
                    </Link>
                    <Typography variant="caption" color={palette.text.secondary}>
                      {act.time}
                    </Typography>
                    <div className="activity-card-footer">
                      <Typography variant="caption" color={palette.text.secondary}>
                        {act.location}
                      </Typography>
                      <Typography variant="body2" color={palette.primary.main} className="activity-stats">
                        {act.duration} hr | {act.distance}
                      </Typography>
                    </div>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color={palette.text.secondary}>
                  No recent activities
                </Typography>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="dashboard-card">
          <CardContent>
            <div className="attendance-header">
              <Typography variant="overline" className="dashboard-card-title">
                TEAM ATTENDANCE
              </Typography>
            </div>
            {attendanceLoading ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress />
              </Box>
            ) : attendanceTeams.length > 0 ? (
              <div className="attendance-list">
                <div className="attendance-teams">
                  {attendanceTeams.map((team, idx) => (
                    <Box
                      key={`${team.teamId}-${idx}`}
                      className="attendance-team-block"
                      sx={{
                        border: `1px solid ${palette.divider}`,
                        backgroundColor: 'background.paper',
                        borderRadius: 2,
                        padding: 2,
                      }}
                    >
                      <Link
                        component="button"
                        variant="body1"
                        underline="hover"
                        color={palette.primary.main}
                        className="attendance-team-name"
                        onClick={() => navigate(`/teams/${team.teamId}/dashboard`)}
                      >
                        {team.teamName}
                      </Link>
                      <div className="attendance-rings">
                        <div className="attendance-ring">
                          <CircularProgressWithLabel
                            value={team.sessionPercent ?? 0}
                            size={48}
                            thickness={4}
                            color="primary"
                          />
                          <span className="attendance-ring-label">
                            Sessions ({team.totalCompleted}/{team.totalAssigned})
                          </span>
                        </div>
                        <div className="attendance-ring">
                          <CircularProgressWithLabel
                            value={team.practicePercent ?? 0}
                            size={48}
                            thickness={4}
                            color="success"
                          />
                          <span className="attendance-ring-label">
                            Practices ({team.totalAttended}/{team.totalMarkedAttended})
                          </span>
                        </div>
                      </div>
                    </Box>
                  ))}
                </div>
              </div>
            ) : (
              <Typography variant="body2" color={palette.text.secondary}>
                No team attendance data
              </Typography>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MyDashboardPage;
